import { useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import {
  listVisibilityChoices,
  setVisibility,
  type Visibility,
  type VisibilityChoice,
  type VisibleKind,
} from '../api/visibility';
import { Alert, Badge, Button, Card } from './ui';
import type { BadgeTone } from './ui';
import './VisibilityControl.css';

export interface VisibilityControlProps {
  kind: VisibleKind;
  id: string;
  value: Visibility;
  /** What this resource is called in a sentence: "dataset", "story", … */
  noun: string;
  onChanged: (visibility: Visibility) => void;
}

const TONES: Record<Visibility, BadgeTone> = {
  PRIVATE: 'neutral',
  UNLISTED: 'accent',
  PUBLIC: 'success',
};

const LABELS: Record<Visibility, string> = {
  PRIVATE: 'Private',
  UNLISTED: 'Unlisted',
  PUBLIC: 'Public',
};

export function VisibilityBadge({ value }: { value: Visibility }) {
  return (
    <Badge tone={TONES[value]} srPrefix="Who can see this">
      {LABELS[value]}
    </Badge>
  );
}

/**
 * Who may see this.
 *
 * Deliberately a decision rather than a toggle: the three states are shown
 * together with what each one actually means, because "public" is the one
 * setting on this product that cannot be quietly undone in someone else's
 * memory. The words come from the backend, so they are the same words it
 * enforces.
 */
export function VisibilityControl({ kind, id, value, noun, onChanged }: VisibilityControlProps) {
  const [choices, setChoices] = useState<VisibilityChoice[]>([]);
  const [saving, setSaving] = useState<Visibility | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void listVisibilityChoices()
      .then(setChoices)
      .catch(() => setChoices([]));
  }, []);

  const choose = async (next: Visibility) => {
    if (next === value) return;
    setSaving(next);
    setError(null);
    try {
      await setVisibility(kind, id, next);
      onChanged(next);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setSaving(null);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Card title="Who can see this" description={`This ${noun} is ${LABELS[value].toLowerCase()}.`}>
      {error && (
        <Alert tone="warning" title="That could not be changed">
          {error.message}
        </Alert>
      )}

      <div className="sc-visibility" role="group" aria-label="Who can see this">
        {choices.map((choice) => (
          <button
            key={choice.value}
            type="button"
            className={`sc-visibility__choice${choice.value === value ? ' is-chosen' : ''}`}
            aria-pressed={choice.value === value}
            disabled={saving !== null}
            onClick={() => void choose(choice.value)}
          >
            <span className="sc-visibility__label">
              <VisibilityBadge value={choice.value} />
              {saving === choice.value && <span className="sc-visibility__saving">saving…</span>}
            </span>
            <span className="sc-visibility__description">{choice.description}</span>
          </button>
        ))}
      </div>

      {value !== 'PRIVATE' && (
        <p className="sc-visibility__link">
          <Button size="sm" variant="secondary" onClick={() => void copyLink()}>
            Copy link
          </Button>
          <span>{copied ? 'Copied. Anyone with it can read this.' : 'Anyone with the link.'}</span>
        </p>
      )}
    </Card>
  );
}
